import * as React from 'react'
import { useCallback, useMemo } from 'react'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import Checkbox from '@mui/material/Checkbox'
import FormControlLabel from '@mui/material/FormControlLabel'
import useTheme from '@mui/material/styles/useTheme'
import useMediaQuery from '@mui/material/useMediaQuery'

import { useGetExternalProductFriendlyName } from '@user/store/authorization/hooks/useGetExternalProductFriendlyName'

import { ScheduleRequirementsProps } from '../../typings'
import { ClosingRequirementCheckbox } from '../../atoms/ClosingRequirementCheckbox'

export const ScheduleRequirement: React.FC<ScheduleRequirementsProps> = (
  props,
) => {
  const {
    onSelected,
    visibilitySettings: {
      freeEstimateScheduleEnabled,
      paidEstimateScheduleEnabled,
      serviceScheduleEnabled,
    },
    ...rest
  } = props

  const anyEventSelected = useMemo(() => {
    return (
      freeEstimateScheduleEnabled ||
      paidEstimateScheduleEnabled ||
      serviceScheduleEnabled
    )
  }, [
    freeEstimateScheduleEnabled,
    paidEstimateScheduleEnabled,
    serviceScheduleEnabled,
  ])

  const onChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
      const name = event.currentTarget.name
      if (name == 'requireServiceSchedule' && !checked) {
        onSelected({
          ...rest,
          requireACWOppsSchedulingService: checked,
          requireACWLeadsSchedulingService: checked,
          [name]: checked,
        })
      } else {
        onSelected({ ...rest, [name]: checked })
      }
    },
    [onSelected, rest],
  )

  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'))

  const externalProductName = useGetExternalProductFriendlyName()

  const autoCloseWinLeadsTitle = `Auto-close/win leads when service is scheduled (${externalProductName} location and other closing requirements still apply). Leads automatically closed/won in this sales funnel will be submitted to the Closed Won Queue.`
  const autoCloseWinTitle = `Auto-close/win opportunities when service is scheduled (${externalProductName} location and other closing requirements still apply). Opportunities automatically closed/won in this sales funnel will be submitted to the Closed Won Queue.`

  const checkboxLabelSx = {
    '& .MuiSvgIcon-root.MuiSvgIcon-fontSizeMedium': {
      fontSize: isDesktop ? 24 : 28,
    },
    '& .MuiFormControlLabel-label': {
      whiteSpace: 'normal',
      wordBreak: 'break-word',
    },
  }

  return (
    <>
      <Box display="flex" alignItems="center" mt={2}>
        <Typography
          variant="h6"
          color={!anyEventSelected ? 'text.disabled' : 'text.primary'}
        >
          Sales Funnel: {rest.salesFunnelName}
        </Typography>
      </Box>
      {freeEstimateScheduleEnabled && (
        <Box mr="25px">
          <FormControlLabel
            label="Free Estimate"
            sx={checkboxLabelSx}
            control={
              <Checkbox
                color="primary"
                checked={rest.requireFreeEstimateSchedule}
                name="requireFreeEstimateSchedule"
                onChange={onChange}
              />
            }
          />
        </Box>
      )}
      {paidEstimateScheduleEnabled && (
        <Box mr="25px">
          <FormControlLabel
            label="Paid Estimate"
            sx={checkboxLabelSx}
            control={
              <Checkbox
                color="primary"
                checked={rest.requirePaidEstimateSchedule}
                name="requirePaidEstimateSchedule"
                onChange={onChange}
              />
            }
          />
        </Box>
      )}
      {serviceScheduleEnabled && (
        <Box mr="25px">
          <FormControlLabel
            label="Service"
            sx={[checkboxLabelSx, { marginBottom: 0 }]}
            control={
              <Checkbox
                color="primary"
                checked={rest.requireServiceSchedule}
                name="requireServiceSchedule"
                onChange={onChange}
              />
            }
          />
          <Box ml="25px">
            <ClosingRequirementCheckbox
              label="Auto-close/win leads upon scheduling service"
              name="requireACWLeadsSchedulingService"
              checked={rest.requireACWLeadsSchedulingService}
              onChange={onChange}
              disabled={!rest.requireServiceSchedule}
              tooltip={autoCloseWinLeadsTitle}
              dataTestId="acw-leads-scheduling-service"
            />
            <ClosingRequirementCheckbox
              label="Auto-close/win opportunities upon scheduling service"
              name="requireACWOppsSchedulingService"
              checked={rest.requireACWOppsSchedulingService}
              onChange={onChange}
              disabled={!rest.requireServiceSchedule}
              tooltip={autoCloseWinTitle}
              dataTestId="acw-opps-scheduling-service"
            />
          </Box>
        </Box>
      )}
    </>
  )
}
ScheduleRequirement.displayName = 'ScheduleRequirement'
